import type { ConnectionState } from '@sd/client-core';
import { splitLinks, type ConversationSummary, type Person } from '@sd/contracts';
import { Fragment, type ReactNode } from 'react';
import { Icon } from '../icons/Icon';
import { Avatar, AvatarGroup } from '../ui/Avatar';
import { useClientState } from './context';
import { parseMarkup, type Inline } from './markup';

/** Person with live presence merged in. */
export function usePerson(id: string | null | undefined): Person | undefined {
  const person = useClientState((s) => (id ? s.people[id] : undefined));
  const online = useClientState((s) => (id ? s.online[id] : undefined));
  if (!person) return undefined;
  return online === undefined || online === person.online ? person : { ...person, online };
}

/** `convAv`: peer avatar for direct chats, two-avatar group otherwise. */
export function ConversationAvatar({ conversation, size }: { conversation: ConversationSummary; size?: number }) {
  const meId = useClientState((s) => s.me?.id);
  const me = usePerson(meId);
  const peer = usePerson(conversation.peer?.id);
  if (conversation.kind === 'direct') return <Avatar person={peer ?? conversation.peer} size={size} />;
  const others = conversation.memberPreview;
  return <AvatarGroup people={[others[0] ?? me, others[1] ?? me]} size={size} off={conversation.my.state !== 'active'} />;
}

const CONN_TEXT: Record<ConnectionState, string> = { online: '연결됨', offline: '연결 끊김', syncing: '동기화 중', connecting: '연결 중' };

/** `.conn-pill` connection status. */
export function ConnectionPill({ compact }: { compact?: boolean }) {
  const connection = useClientState((s) => s.connection);
  const cls = connection === 'online' ? '' : connection === 'offline' ? 'offline' : 'syncing';
  return (
    <span className={`conn-pill ${cls}`.trim()} style={compact ? { height: 26 } : undefined} role="status">
      <span className="dot" />
      <span className="ct">{CONN_TEXT[connection]}</span>
    </span>
  );
}

/**
 * Message text with its formatting (markup.ts: bold, strikethrough, code), @mentions of known members
 * highlighted and http(s) links. Plain React text nodes only — never HTML injection. Links are reached with
 * Tab only while their message is the timeline's Tab stop (`tabbable`).
 */
export function RichText({ text, mentionNames, tabbable }: { text: string; mentionNames: readonly string[]; tabbable: boolean }) {
  const names = [...mentionNames].filter(Boolean).sort((a, b) => b.length - a.length);
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const mentionRe = escaped.length ? new RegExp(`@(${escaped.join('|')})`, 'g') : null;
  const words = (value: string) =>
    splitTokens(value, mentionRe).map((part, pi) =>
      part.kind === 'mention' ? (
        <span key={pi} className="mtag">
          {part.text}
        </span>
      ) : part.kind === 'link' ? (
        <a key={pi} href={part.text} target="_blank" rel="noopener noreferrer" tabIndex={tabbable ? undefined : -1}>
          {part.text}
        </a>
      ) : (
        <Fragment key={pi}>{part.text}</Fragment>
      ),
    );
  const inline = (parts: readonly Inline[]): ReactNode[] =>
    parts.map((part, i) =>
      part.kind === 'code' ? (
        <code key={i} className="icode">
          {part.text}
        </code>
      ) : part.kind === 'strong' ? (
        <strong key={i}>{inline(part.children)}</strong>
      ) : part.kind === 'strike' ? (
        <s key={i}>{inline(part.children)}</s>
      ) : (
        <Fragment key={i}>{words(part.text)}</Fragment>
      ),
    );
  return (
    <>
      {parseMarkup(text).map((block, bi) =>
        block.kind === 'code' ? (
          <pre key={bi} className="codeblock">
            <code>{block.text}</code>
          </pre>
        ) : (
          <Fragment key={bi}>
            {block.lines.map((line, li) => (
              <Fragment key={li}>
                {li > 0 ? <br /> : null}
                {inline(line)}
              </Fragment>
            ))}
          </Fragment>
        ),
      )}
    </>
  );
}

type Token = { kind: 'text' | 'mention' | 'link'; text: string };

function splitTokens(line: string, mentionRe: RegExp | null): Token[] {
  const out: Token[] = [];
  // The same links the server previews: sentence punctuation after a link is not part of it.
  for (const { text: piece, link } of splitLinks(line)) {
    if (link) {
      out.push({ kind: 'link', text: piece });
      continue;
    }
    if (!mentionRe) {
      out.push({ kind: 'text', text: piece });
      continue;
    }
    let last = 0;
    for (const match of piece.matchAll(mentionRe)) {
      if (match.index > last) out.push({ kind: 'text', text: piece.slice(last, match.index) });
      out.push({ kind: 'mention', text: match[0] });
      last = match.index + match[0].length;
    }
    if (last < piece.length) out.push({ kind: 'text', text: piece.slice(last) });
  }
  return out;
}

/** Highlights the first occurrence of `q` with `<mark>` (search results). */
export function Highlight({ text, q }: { text: string; q: string }): ReactNode {
  if (!q) return text;
  const index = text.indexOf(q);
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark>{q}</mark>
      {text.slice(index + q.length)}
    </>
  );
}

/** A list in a popover (who reacted, who read) that could not be loaded: says so, announced, and offers another try. */
export function LoadFailed({ onRetry }: { onRetry(): void }) {
  return (
    <>
      <div role="alert">
        <h2>불러오지 못했어요</h2>
      </div>
      <button
        type="button"
        className="pi"
        onClick={(event) => {
          // The button goes while the list loads again: focus stays in the popover.
          event.currentTarget.closest<HTMLElement>('.pop')?.focus({ preventScroll: true });
          onRetry();
        }}
      >
        <Icon name="retry" size="sm" />
        다시 시도
      </button>
    </>
  );
}
